import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Download, Edit, Trash2, Bell, Search, MessageCircle, CalendarClock, CalendarRange, CircleDot, Handshake, DollarSign, XCircle, ChevronDown, ChevronUp, Store, Copy, Users, ShoppingBag, Check } from "lucide-react";
import LojaSelector from "@/components/LojaSelector";
import { useLojas, useLojasInfo } from "@/hooks/useLojas";
import { logError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOTIVOS = ["Orçamento", "Consulta", "Não retornou", "Outros"];
const ORIGENS = ["Tráfego", "Influencer", "Instagram", "Não identificado"];

const CAMPOS_DATA = [
  { value: "ultimo_contato", label: "Último contato" },
  { value: "cadastro", label: "Cadastro" },
  { value: "proximo_retorno", label: "Próximo retorno" },
];

const PERIODOS = [
  { value: "todos", label: "Todos" },
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "mes", label: "Mês atual" },
  { value: "custom", label: "Personalizado" },
];
const STATUS = ["Ativo", "Em Negociação", "Venda", "Inativo"];
const MSG_POS_VENDA = "Olá, tudo bem? \nPassando para saber como está a adaptação com seu óculos novo, caso precise de algum ajuste na armação também estamos a disposição 🙏🏻";
const STATUS_TABS = [
  { key: "Todos", color: "slate", Icon: null },
  { key: "Ativo", color: "emerald", Icon: CircleDot },
  { key: "Em Negociação", color: "amber", Icon: Handshake },
  { key: "Venda", color: "blue", Icon: DollarSign },
  { key: "Inativo", color: "slate", Icon: XCircle },
];
const STATUS_COR = {
  "Ativo": "bg-emerald-100 text-emerald-800",
  "Em Negociação": "bg-amber-100 text-amber-800",
  "Venda": "bg-blue-100 text-blue-800",
  "Inativo": "bg-slate-200 text-slate-700",
};
const ROW_BG = {
  "Ativo": "hover:bg-emerald-50/50",
  "Em Negociação": "hover:bg-amber-50/50",
  "Venda": "hover:bg-blue-50/50",
  "Inativo": "bg-slate-50/60 hover:bg-slate-100/60",
};

const hojeStr = () => new Date().toISOString().slice(0, 10);
const addDia = (d) => {
  if (!d) return "";
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  dt.setDate(dt.getDate() + 1);
  return dt.toISOString().slice(0, 10);
};

const novoVazio = () => ({
  nome: "", telefone: "", motivo: "Orçamento", motivo_detalhe: "", origem: "Tráfego",
  status: "Ativo", data_ultimo_contato: hojeStr(), proximo_retorno: addDia(hojeStr()), observacoes: "", nome_loja: "",
});

const abrirWhats = (c, empresa) => {
  const digits = String(c.telefone || "").replace(/\D/g, "");
  if (!digits) { toast.error("Contato sem telefone"); return; }
  const numero = digits.startsWith("55") ? digits : `55${digits}`;
  const msg = `Olá${c.nome ? " " + c.nome : ""}! Aqui é da ${empresa || "nossa ótica"}. Passando para dar continuidade ao seu atendimento. Como podemos te ajudar?`;
  window.open(`https://wa.me/${numero}?text=${encodeURIComponent(msg)}`, "_blank");
};

const copiarTelefone = async (tel) => {
  const t = String(tel || "").replace(/\D/g, "");
  if (!t) { toast.error("Sem telefone"); return; }
  try { await navigator.clipboard.writeText(t); toast.success("Telefone copiado"); }
  catch { toast.error("Não foi possível copiar"); }
};

function TabBtn({ active, onClick, testid, color, Icon, children }) {
  const cores = {
    slate: "border-slate-900 text-slate-900",
    emerald: "border-emerald-500 text-emerald-600",
    amber: "border-amber-500 text-amber-600",
    blue: "border-blue-500 text-blue-600",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${
        active ? cores[color] : "border-transparent text-slate-400 hover:text-slate-600"
      }`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}{children}
    </button>
  );
}


export default function CRM() {
  const LOJAS = useLojas();
  const { empresa: empresaNome } = useLojasInfo();
  const [contatos, setContatos] = useState([]);
  const [resumo, setResumo] = useState({ retorno_hoje: { total: 0, por_loja: {} }, alertas: [] });
  const [lojaFiltro, setLojaFiltro] = useState("Todas as lojas");
  const [busca, setBusca] = useState("");
  const [abaStatus, setAbaStatus] = useState("Todos");
  const [filtroOrigem, setFiltroOrigem] = useState("");
  const [modalAberto, setModalAberto] = useState(false);
  const [pendentesAberto, setPendentesAberto] = useState(false);
  const [lojaSelecionadaGate, setLojaSelecionadaGate] = useState(false);
  const [modo, setModo] = useState("contatos"); // contatos | pos_venda
  const [posVenda, setPosVenda] = useState([]);
  const [form, setForm] = useState(novoVazio());
  const [editId, setEditId] = useState(null);
  const [campoData, setCampoData] = useState("ultimo_contato");
  const [periodo, setPeriodo] = useState("todos");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  const paramsFiltro = useCallback(() => {
    const p = {};
    if (lojaFiltro !== "Todas as lojas") p.loja = lojaFiltro;
    if (filtroOrigem) p.origem = filtroOrigem;
    if (busca) p.busca = busca;
    if (abaStatus !== "Todos") p.status = abaStatus;
    if (dataInicio || dataFim) {
      p.campo_data = campoData;
      if (dataInicio) p.data_inicio = dataInicio;
      if (dataFim) p.data_fim = dataFim;
    }
    return p;
  }, [lojaFiltro, filtroOrigem, busca, abaStatus, campoData, dataInicio, dataFim]);

  const aplicarPeriodo = (p) => {
    setPeriodo(p);
    const hoje = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (p === "todos") { setDataInicio(""); setDataFim(""); return; }
    if (p === "hoje") { setDataInicio(iso(hoje)); setDataFim(iso(hoje)); return; }
    if (p === "7d" || p === "30d") {
      const ini = new Date(hoje);
      ini.setDate(ini.getDate() - (p === "7d" ? 6 : 29));
      setDataInicio(iso(ini)); setDataFim(iso(hoje)); return;
    }
    if (p === "mes") {
      setDataInicio(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-01`);
      setDataFim(iso(hoje));
    }
  };

  const carregar = useCallback(async () => {
    try {
      const params = paramsFiltro();
      delete params.status; // as abas de status filtram na tela (mantém as contagens)
      const res = await axios.get(`${API}/crm/contatos`, { params });
      setContatos(res.data);
    } catch {
      toast.error("Erro ao carregar contatos");
    }
  }, [paramsFiltro]);

  const carregarResumo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/resumo`);
      setResumo(res.data);
      window.dispatchEvent(new Event("crm-updated"));
    } catch (e) {
      logError("Erro ao carregar resumo do CRM:", e);
    }
  }, []);

  const carregarPosVenda = useCallback(async () => {
    try {
      const params = {};
      if (lojaFiltro !== "Todas as lojas") params.loja = lojaFiltro;
      const res = await axios.get(`${API}/crm/retornos-compra`, { params });
      setPosVenda(res.data.retornos || []);
    } catch {
      toast.error("Erro ao carregar retornos de compra");
    }
  }, [lojaFiltro]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarResumo(); }, [carregarResumo]);
  useEffect(() => { if (modo === "pos_venda") carregarPosVenda(); }, [modo, carregarPosVenda]);

  const abrirNovo = () => {
    setForm({ ...novoVazio(), nome_loja: lojaFiltro !== "Todas as lojas" ? lojaFiltro : (LOJAS[0] || "") });
    setEditId(null);
    setModalAberto(true);
  };
  const abrirEdicao = (c) => { setForm({ ...novoVazio(), ...c }); setEditId(c.id); setModalAberto(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const salvar = async () => {
    if (!form.telefone) { toast.error("Telefone é obrigatório"); return; }
    try {
      if (editId) await axios.put(`${API}/crm/contatos/${editId}`, form);
      else await axios.post(`${API}/crm/contatos`, form);
      toast.success(editId ? "Contato atualizado" : "Contato cadastrado");
      setModalAberto(false);
      carregar();
      carregarResumo();
    } catch {
      toast.error("Erro ao salvar contato");
    }
  };

  const remover = async (id) => {
    try {
      await axios.delete(`${API}/crm/contatos/${id}`);
      toast.success("Removido");
      carregar();
      carregarResumo();
    } catch { toast.error("Erro ao remover"); }
  };

  const alternarStatus = async (c) => {
    const prox = STATUS[(STATUS.indexOf(c.status) + 1) % STATUS.length];
    setContatos((lista) => lista.map((x) => (x.id === c.id ? { ...x, status: prox } : x)));
    try {
      await axios.put(`${API}/crm/contatos/${c.id}`, { status: prox });
      toast.success(`Status: ${prox}`);
      carregarResumo();
    } catch {
      toast.error("Erro ao alterar status");
      carregar();
    }
  };

  const setStatusContato = async (id, novo) => {
    setContatos((l) => l.map((x) => (x.id === id ? { ...x, status: novo } : x)));
    try {
      await axios.put(`${API}/crm/contatos/${id}`, { status: novo });
      toast.success(`Status: ${novo}`);
      carregarResumo();
    } catch {
      toast.error("Erro ao alterar status");
      carregar();
    }
  };

  const atualizarCampo = async (id, campo, valor) => {
    setContatos((lista) => lista.map((x) => (x.id === id ? { ...x, [campo]: valor } : x)));
    try {
      await axios.put(`${API}/crm/contatos/${id}`, { [campo]: valor });
      carregarResumo();
    } catch {
      toast.error("Erro ao atualizar data");
      carregar();
    }
  };

  // Retornos de compra (pós-venda 40 dias)
  const enviarPosVenda = (c) => {
    const digits = String(c.telefone_cliente || "").replace(/\D/g, "");
    if (!digits) { toast.error("Cliente sem telefone"); return; }
    const numero = digits.startsWith("55") ? digits : `55${digits}`;
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(MSG_POS_VENDA)}`, "_blank");
  };
  const marcarContatado = async (id) => {
    setPosVenda((lista) => lista.filter((x) => x.id !== id));
    try {
      await axios.post(`${API}/crm/retornos-compra/${id}/contatado`);
      toast.success("Marcado como contatado");
    } catch {
      toast.error("Erro ao marcar");
      carregarPosVenda();
    }
  };

  const exportar = async () => {
    try {
      const params = paramsFiltro();
      const res = await axios.get(`${API}/crm/contatos/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const sufixoData = dataInicio || dataFim ? `_${dataInicio || "inicio"}_a_${dataFim || "fim"}` : "";
      a.download = `contatos_crm_${lojaFiltro.toLowerCase().replace(/ /g, "_")}${sufixoData}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado${dataInicio || dataFim ? " com o período filtrado" : ""}`);
    } catch { toast.error("Erro ao exportar"); }
  };

  const fmtData = (d) => d ? String(d).slice(0, 10).split("-").reverse().join("/") : "-";
  const lojasPendentesAll = Object.entries(resumo.retorno_hoje?.por_loja || {});
  const lojasPendentes = lojaFiltro === "Todas as lojas"
    ? lojasPendentesAll
    : lojasPendentesAll.filter(([loja]) => loja === lojaFiltro);
  const totalPendentes = lojasPendentes.reduce((n, [, itens]) => n + itens.length, 0);
  const contarStatus = (s) => contatos.filter((c) => c.status === s).length;
  const contatosFiltrados = abaStatus === "Todos" ? contatos : contatos.filter((c) => c.status === abaStatus);

  if (!lojaSelecionadaGate) {
    const opcoes = [
      { value: "Todas as lojas", label: "Gerencial (todas as lojas)", gerencial: true },
      ...LOJAS.map((l) => ({ value: l, label: l })),
    ];
    const escolher = (v) => { setLojaFiltro(v); setLojaSelecionadaGate(true); };
    return (
      <div className="max-w-2xl mx-auto" data-testid="crm-loja-gate">
        <div className="text-center mb-8 mt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-4"><Store className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">CRM — Selecione a loja</h1>
          <p className="text-sm text-slate-500 mt-1">Escolha uma loja para acompanhar os contatos ou entre no modo gerencial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {opcoes.map((o) => (
            <button key={o.value} onClick={() => escolher(o.value)}
              data-testid={`crm-gate-${o.value.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left hover:shadow-md ${
                o.gerencial ? "border-slate-900 bg-slate-900 text-white sm:col-span-2 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
              }`}>
              <Store className={`w-5 h-5 shrink-0 ${o.gerencial ? "text-white" : "text-slate-500"}`} />
              <span className="font-semibold">{o.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="crm-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">CRM — Acompanhamento de Contatos</h1>
          <p className="text-sm text-slate-500">Gestão de contatos via WhatsApp por loja{lojaFiltro !== "Todas as lojas" ? ` · ${lojaFiltro}` : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setLojaSelecionadaGate(false); setModo("contatos"); }} data-testid="crm-trocar-loja-btn"><Store className="w-4 h-4 mr-2" />Trocar loja</Button>
          {modo === "contatos" && <Button variant="outline" onClick={exportar} data-testid="crm-export-btn"><Download className="w-4 h-4 mr-2" />Exportar Excel</Button>}
          {modo === "contatos" && <Button onClick={abrirNovo} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="crm-novo-btn"><Plus className="w-4 h-4 mr-2" />Cadastrar</Button>}
        </div>
      </div>

      {/* Alternância de modo */}
      <div className="flex gap-2 mb-5" data-testid="crm-modo-tabs">
        <button onClick={() => setModo("contatos")} data-testid="crm-modo-contatos"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all border ${
            modo === "contatos" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}>
          <Users className="w-4 h-4" /> Contatos
        </button>
        <button onClick={() => setModo("pos_venda")} data-testid="crm-modo-pos-venda"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all border ${
            modo === "pos_venda" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}>
          <ShoppingBag className="w-4 h-4" /> Retornos de compra
          {posVenda.length > 0 && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${modo === "pos_venda" ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800"}`}>{posVenda.length}</span>}
        </button>
      </div>

      {modo === "pos_venda" ? (
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="crm-pos-venda">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <ShoppingBag className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Acompanhamento de adaptação</p>
                <p className="text-xs text-amber-700 mt-0.5">Vendas finalizadas há 40+ dias. Envie a mensagem de pós-venda e marque como contatado.</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead>Loja</TableHead><TableHead>OS</TableHead><TableHead>Cliente</TableHead>
                    <TableHead>Telefone</TableHead><TableHead>Data da Venda</TableHead><TableHead>Dias</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posVenda.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhum retorno de compra pendente</TableCell></TableRow>}
                  {posVenda.map((c) => (
                    <TableRow key={c.id} data-testid={`crm-pos-row-${c.id}`}>
                      <TableCell className="text-slate-600">{c.nome_loja || "-"}</TableCell>
                      <TableCell className="font-mono">{c.os || "-"}</TableCell>
                      <TableCell className="font-medium">{c.nome_cliente || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span>{c.telefone_cliente || "-"}</span>
                          {c.telefone_cliente && (
                            <button onClick={() => copiarTelefone(c.telefone_cliente)} title="Copiar telefone"
                              className="inline-flex items-center justify-center h-6 w-6 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors shrink-0"
                              data-testid={`crm-pos-copy-${c.id}`}>
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{fmtData(c.data_venda)}</TableCell>
                      <TableCell><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{c.dias} dias</span></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5 justify-end">
                          <button onClick={() => enviarPosVenda(c)} title="Enviar mensagem de pós-venda no WhatsApp"
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors text-xs font-semibold"
                            data-testid={`crm-pos-wa-${c.id}`}>
                            <MessageCircle className="w-3.5 h-3.5" /> Mensagem
                          </button>
                          <button onClick={() => marcarContatado(c.id)} title="Marcar como contatado"
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-semibold"
                            data-testid={`crm-pos-done-${c.id}`}>
                            <Check className="w-3.5 h-3.5" /> Contatado
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Painel de Pendentes — recolhível */}
      <Card className="mb-4 bg-white border-slate-200 shadow-sm" data-testid="crm-painel-pendentes">
        <CardContent className="pt-4">
          <button onClick={() => setPendentesAberto((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="crm-pendentes-toggle">
            <CalendarClock className="w-4 h-4 text-amber-600" />
            <h2 className="text-base font-semibold text-slate-800">Retornos pendentes <span className="font-normal text-slate-400">(hoje e atrasados)</span></h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="crm-pendentes-total">
              {totalPendentes}
            </span>
            <span className="ml-auto text-slate-400">
              {pendentesAberto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </button>
          {pendentesAberto && (
            lojasPendentes.length === 0 ? (
              <p className="text-sm text-slate-400 mt-3">Nenhum retorno pendente.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                {lojasPendentes.map(([loja, itens]) => (
                  <div key={loja} className="rounded-lg border border-slate-200 p-3" data-testid={`crm-pendentes-loja-${loja}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-700">{loja}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">{itens.length}</span>
                    </div>
                    <ul className="space-y-2">
                      {itens.map((c) => (
                        <li key={c.id} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-xs font-medium text-slate-700">
                              {c.atrasado && <span className="text-red-500 font-bold mr-1" title="Atrasado">!</span>}
                              {c.nome || c.telefone}
                            </span>
                            <div className="flex items-center gap-1 shrink-0">
                              {c.status && (
                                <select value={c.status} onChange={(e) => setStatusContato(c.id, e.target.value)}
                                  className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white" data-testid={`crm-pendente-status-${c.id}`}>
                                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              )}
                              <button onClick={() => copiarTelefone(c.telefone)} title="Copiar telefone"
                                className="inline-flex items-center justify-center h-6 w-6 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                data-testid={`crm-pendente-copy-${c.id}`}>
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => abrirWhats(c, empresaNome)} title="Chamar no WhatsApp"
                                className="inline-flex items-center justify-center h-6 w-6 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                                data-testid={`crm-pendente-wa-${c.id}`}>
                                <MessageCircle className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[11px] font-mono text-slate-500">{c.telefone}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-1">
                            <span className="text-[11px] text-slate-500 truncate">
                              {c.motivo}{c.motivo === "Outros" && c.motivo_detalhe ? `: ${c.motivo_detalhe}` : ""} · contato {fmtData(c.data_ultimo_contato)}
                            </span>
                            <input type="date" value={(c.proximo_retorno || "").slice(0, 10)}
                              onChange={(e) => atualizarCampo(c.id, "proximo_retorno", e.target.value)}
                              className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white shrink-0"
                              title="Alterar próximo retorno" data-testid={`crm-pendente-retorno-${c.id}`} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card className="mb-4 bg-white border-slate-200 shadow-sm">
        <CardContent className="pt-4 flex flex-wrap items-center gap-3">
          <LojaSelector lojaSelecionada={lojaFiltro} onLojaChange={setLojaFiltro} todasLabel="Gerencial (todas as lojas)" />
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input placeholder="Buscar por nome ou telefone" value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="crm-busca" />
          </div>
          <Select value={filtroOrigem || "all"} onValueChange={(v) => setFiltroOrigem(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="crm-filtro-origem"><SelectValue placeholder="Origem" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas as origens</SelectItem>{ORIGENS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
        <CardContent className="pt-0 pb-4 border-t border-slate-100 mt-1">
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Período por</span>
            <Select value={campoData} onValueChange={setCampoData}>
              <SelectTrigger className="w-44 h-9" data-testid="crm-campo-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CAMPOS_DATA.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {PERIODOS.map((p) => (
              <button key={p.value} onClick={() => aplicarPeriodo(p.value)}
                data-testid={`crm-periodo-${p.value}`}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold border transition-all ${
                  periodo === p.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}>
                <CalendarRange className="w-3.5 h-3.5" />{p.label}
              </button>
            ))}
            <div className={`flex items-center gap-2 ${periodo === "custom" ? "" : "hidden"}`}>
              <Input type="date" value={dataInicio} onChange={(e) => { setDataInicio(e.target.value); setPeriodo("custom"); }} className="h-9 w-40" data-testid="crm-data-inicio" />
              <span className="text-slate-400 text-sm">até</span>
              <Input type="date" value={dataFim} onChange={(e) => { setDataFim(e.target.value); setPeriodo("custom"); }} className="h-9 w-40" data-testid="crm-data-fim" />
            </div>
            {(dataInicio || dataFim) && (
              <span className="text-xs text-slate-500" data-testid="crm-periodo-ativo">
                {contatos.length} contato(s) · {fmtData(dataInicio) || "início"} a {fmtData(dataFim) || "hoje"}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cabeçalho de Status */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto mb-4" data-testid="crm-status-tabs">
        {STATUS_TABS.map((t) => (
          <TabBtn key={t.key} active={abaStatus === t.key} onClick={() => setAbaStatus(t.key)}
            testid={`crm-tab-${t.key.toLowerCase().replace(/ /g, "-").replace(/ç/g, "c").replace(/ã/g, "a")}`}
            color={t.color} Icon={t.Icon}>
            {`${t.key} (${t.key === "Todos" ? contatos.length : contarStatus(t.key)})`}
          </TabBtn>
        ))}
      </div>

      {/* Tabela */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Telefone</TableHead><TableHead>Nome</TableHead><TableHead>Motivo</TableHead>
                <TableHead>Origem</TableHead><TableHead>Status</TableHead><TableHead>Último Contato</TableHead>
                <TableHead>Próximo Retorno</TableHead><TableHead>Observações</TableHead><TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contatosFiltrados.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Nenhum contato</TableCell></TableRow>}
              {contatosFiltrados.map((c) => (
                <TableRow key={c.id} className={ROW_BG[c.status] || ""} data-testid={`crm-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1.5">
                      {c.precisa_atualizar && <Bell className="w-3.5 h-3.5 text-amber-500" title="Atualização pendente (2+ dias)" data-testid={`crm-alerta-${c.id}`} />}
                      <span>{c.telefone}</span>
                      <button onClick={() => copiarTelefone(c.telefone)} title="Copiar telefone"
                        className="inline-flex items-center justify-center h-6 w-6 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors shrink-0"
                        data-testid={`crm-copy-${c.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => abrirWhats(c, empresaNome)} title="Chamar no WhatsApp"
                        className="inline-flex items-center justify-center h-6 w-6 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors shrink-0"
                        data-testid={`crm-wa-${c.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>{c.nome || "-"}</TableCell>
                  <TableCell>{c.motivo}{c.motivo === "Outros" && c.motivo_detalhe ? `: ${c.motivo_detalhe}` : ""}</TableCell>
                  <TableCell>{c.origem}</TableCell>
                  <TableCell>
                    <Select value={c.status} onValueChange={(v) => setStatusContato(c.id, v)}>
                      <SelectTrigger className={`h-7 w-auto gap-1 px-2 text-xs font-semibold border-0 rounded-full ${STATUS_COR[c.status] || ""}`} data-testid={`crm-status-select-${c.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <input type="date" value={(c.data_ultimo_contato || "").slice(0, 10)}
                      onChange={(e) => atualizarCampo(c.id, "data_ultimo_contato", e.target.value)}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                      title="Alterar data do contato inicial" data-testid={`crm-col-ultimo-${c.id}`} />
                  </TableCell>
                  <TableCell>
                    <input type="date" value={(c.proximo_retorno || "").slice(0, 10)}
                      onChange={(e) => atualizarCampo(c.id, "proximo_retorno", e.target.value)}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                      title="Alterar próximo retorno" data-testid={`crm-col-retorno-${c.id}`} />
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate" title={c.observacoes}>{c.observacoes || "-"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => abrirEdicao(c)} data-testid={`crm-edit-${c.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remover(c.id)} data-testid={`crm-del-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="crm-modal">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Contato" : "Novo Contato"}</DialogTitle>
            <DialogDescription>Preencha os dados do contato de WhatsApp para acompanhamento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefone *</Label><Input value={form.telefone} onChange={(e) => set("telefone", e.target.value)} placeholder="47999998888" data-testid="crm-form-telefone" /></div>
              <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="crm-form-nome" /></div>
            </div>
            <div><Label>Loja</Label>
              <Select value={form.nome_loja} onValueChange={(v) => set("nome_loja", v)}>
                <SelectTrigger data-testid="crm-form-loja"><SelectValue /></SelectTrigger>
                <SelectContent>{LOJAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Motivo</Label>
                <Select value={form.motivo} onValueChange={(v) => set("motivo", v)}>
                  <SelectTrigger data-testid="crm-form-motivo"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOTIVOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Origem</Label>
                <Select value={form.origem} onValueChange={(v) => set("origem", v)}>
                  <SelectTrigger data-testid="crm-form-origem"><SelectValue /></SelectTrigger>
                  <SelectContent>{ORIGENS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.motivo === "Outros" && (
              <div><Label>Detalhe do motivo</Label><Input value={form.motivo_detalhe} onChange={(e) => set("motivo_detalhe", e.target.value)} data-testid="crm-form-motivo-detalhe" /></div>
            )}
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="crm-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data último contato</Label><Input type="date" value={(form.data_ultimo_contato || "").slice(0, 10)} onChange={(e) => setForm((f) => ({ ...f, data_ultimo_contato: e.target.value, proximo_retorno: addDia(e.target.value) }))} data-testid="crm-form-ultimo" /></div>
              <div><Label>Próximo retorno</Label><Input type="date" value={(form.proximo_retorno || "").slice(0, 10)} onChange={(e) => set("proximo_retorno", e.target.value)} data-testid="crm-form-retorno" /><p className="text-[11px] text-slate-400 mt-1">Preenchido automaticamente (1 dia após o contato)</p></div>
            </div>
            <div><Label>Observações</Label><Textarea value={form.observacoes} onChange={(e) => set("observacoes", e.target.value)} data-testid="crm-form-obs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button>
            <Button onClick={salvar} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="crm-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
